import { useState, useMemo } from "react";
import { Search, ArrowRight } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PRODUCTS } from "@/data/products";
import { useLang } from "@/contexts/LanguageContext";
import { PRODUCTS as PIDS } from "@/constants/testIds";

const IMAGES = {
  beverages:
    "https://images.pexels.com/photos/14609146/pexels-photo-14609146.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  sweet:
    "https://images.unsplash.com/photo-1599629974232-2365495b9ef2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwxfHxnb3VybWV0JTIwc25hY2tzfGVufDB8fHx8MTc4MDU3MTgxMXww&ixlib=rb-4.1.0&q=85&w=900",
  salty:
    "https://images.pexels.com/photos/6660060/pexels-photo-6660060.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
};

const Products = () => {
  const { t, lang } = useLang();
  const [tab, setTab] = useState("beverages");
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return PRODUCTS.filter(
      (p) =>
        p.category === tab &&
        (q === "" ||
          p.name.toLowerCase().includes(q) ||
          p.brand.toLowerCase().includes(q))
    );
  }, [tab, query]);

  const scrollToQuote = () => {
    const el = document.getElementById("quote");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const tabConfig = [
    { value: "beverages", label: t.products.tabs.beverages, testId: PIDS.tabBeverages },
    { value: "sweet", label: t.products.tabs.sweet, testId: PIDS.tabSweet },
    { value: "salty", label: t.products.tabs.salty, testId: PIDS.tabSalty },
  ];

  return (
    <section
      id="products"
      data-testid={PIDS.root}
      className="section-pad bg-[#FAFAF8]"
    >
      <div className="container-cust">
        <div className="grid lg:grid-cols-12 gap-12 mb-12 lg:mb-16">
          <div className="lg:col-span-7">
            <p className="eyebrow mb-5">
              <span className="eyebrow-line"></span>
              {t.products.eyebrow}
            </p>
            <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#1A2421] leading-tight">
              {t.products.title}
            </h2>
          </div>
          <div className="lg:col-span-5 flex items-end">
            <p className="text-[#4A5D58] text-base md:text-lg leading-relaxed">
              {t.products.subtitle}
            </p>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab} data-testid={PIDS.tabs}>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-10">
            <TabsList className="bg-[#F3F3EF] p-1 rounded-full h-auto">
              {tabConfig.map((tc) => (
                <TabsTrigger
                  key={tc.value}
                  value={tc.value}
                  data-testid={tc.testId}
                  className="rounded-full px-5 py-2.5 text-sm data-[state=active]:bg-[#0E5A4E] data-[state=active]:text-white text-[#4A5D58]"
                >
                  {tc.label}
                </TabsTrigger>
              ))}
            </TabsList>

            <div className="relative md:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4A5D58]" />
              <Input
                data-testid={PIDS.search}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t.products.searchPlaceholder}
                className="pl-9 h-11 bg-white border-[#E2E2DB] rounded-full"
              />
            </div>
          </div>

          {tabConfig.map((tc) => (
            <TabsContent key={tc.value} value={tc.value} className="mt-0">
              <div className="grid lg:grid-cols-12 gap-10 items-start">
                <div className="lg:col-span-4 lg:sticky lg:top-28 self-start">
                  <div className="overflow-hidden rounded-sm">
                    <img
                      src={IMAGES[tc.value]}
                      alt={tc.label}
                      className="w-full h-72 lg:h-[440px] object-cover"
                    />
                  </div>
                  <p className="mt-5 text-xs text-[#4A5D58] uppercase tracking-widest">
                    {t.products.noPriceNotice}
                  </p>
                  <Button
                    data-testid={PIDS.ctaQuote}
                    onClick={scrollToQuote}
                    className="mt-6 bg-[#0E5A4E] hover:bg-[#0B4A40] text-white rounded-full px-6 h-11"
                  >
                    {t.products.ctaQuote}
                    <ArrowRight className="ml-1 w-4 h-4" />
                  </Button>
                </div>

                <div className="lg:col-span-8">
                  {filtered.length === 0 ? (
                    <div className="text-center py-16 text-[#4A5D58]">
                      {t.products.empty}
                    </div>
                  ) : (
                    <ul className="divide-y divide-[#E2E2DB] border-y border-[#E2E2DB]">
                      {filtered.map((p) => (
                        <li
                          key={p.id}
                          data-testid={PIDS.item(p.id)}
                          className="py-5 group flex items-start justify-between gap-6 hover:bg-white transition-colors px-2 -mx-2"
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-3 flex-wrap">
                              <h3 className="font-serif-display text-lg md:text-xl text-[#1A2421] group-hover:text-[#0E5A4E] transition-colors">
                                {p.name}
                              </h3>
                              <Badge
                                variant="outline"
                                className="border-[#E2E2DB] text-[10px] uppercase tracking-widest text-[#4A5D58] font-normal"
                              >
                                {p.brand}
                              </Badge>
                            </div>
                            <p className="mt-1.5 text-sm text-[#4A5D58] leading-relaxed">
                              {p.desc[lang] || p.desc.it}
                            </p>
                          </div>
                          <span className="font-serif-display text-[#D4AF37] text-2xl leading-none opacity-0 group-hover:opacity-100 transition-opacity">
                            ·
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}

                  <div className="mt-6 flex items-center justify-between flex-wrap gap-3">
                    <p className="font-serif-display italic text-[#0E5A4E] text-base md:text-lg">
                      {t.products.andMore}
                    </p>
                    <span className="text-xs text-[#4A5D58]">
                      {filtered.length} {filtered.length === 1 ? "ref." : "refs."}
                    </span>
                  </div>
                </div>
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </section>
  );
};

export default Products;
